import type {
  NavigationContextChange,
  NavigationContextResult,
} from '@github-ui/react-app-navigation/navigation-context-handler'
import {getRouteContext, isSameRouteContext} from '@github-ui/react-app-navigation/route-context'

import {getDisplayedContext, navigateWithContextRefresh} from './navigate-with-context-refresh'

/**
 * Handles in-app navigations whose destination route declares a different context than the current
 * route. It navigates with a context refresh, or declines so the page reloads when the context can't be refreshed.
 */
export function handleRouteContextNavigation({
  destination,
  from,
  to,
  options,
}: NavigationContextChange): NavigationContextResult {
  const source = getRouteContext(from)
  const target = getRouteContext(to)
  if (!source || !target || isSameRouteContext(source.context, target.context)) return 'unchanged'

  // The context refresh adds a history entry, so it can't replace the current one.
  if (options?.replace) return 'declined'
  // Only presets render without local navigation tabs.
  if (target.context.crumbType !== 'preset' && !target.selectedLink) return 'declined'

  const navigated = navigateWithContextRefresh({
    // The route's tab is current after in-app tab changes; the page's `selected-link` meta tag isn't.
    sourceContext: getDisplayedContext(source.context, source.selectedLink),
    destinationContext: {
      ...target.context,
      path: destination,
      ...(target.selectedLink ? {selectedLink: target.selectedLink} : {}),
    },
    state: options?.state as Record<string, unknown> | undefined,
  })
  return navigated ? 'navigated' : 'declined'
}
